import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DayOfWeek, FacultyTimetable, WORKING_DAYS } from "../academicOperations/types";
import { LeaveHalfDay, LeaveRequest, LeaveType } from "./types";

dayjs.extend(customParseFormat);

export const LEAVE_TEXT_MAX = 300;

const DAY_NAMES: (DayOfWeek | "SUNDAY")[] = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
const dayOf = (date: Dayjs) => DAY_NAMES[date.day()];

/** Academic year dates arrive as "DD-MM-YYYY" (or ISO). */
export const parseYearDate = (value?: string | null): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value, ["DD-MM-YYYY", "YYYY-MM-DD"], true);
  return parsed.isValid() ? parsed : null;
};

export interface LeaveForm {
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  halfDay: LeaveHalfDay | null;
  leaveType: LeaveType | "";
  reason: string;
}

export type LeaveFormErrors = Partial<Record<"fromDate" | "toDate" | "halfDay" | "leaveType" | "reason" | "form", string>>;

export interface LeaveRulesContext {
  year: { name: string; start: Dayjs; end: Dayjs } | null;
  workingDays: DayOfWeek[]; // the school's working days (any grade has periods); Mon–Sat when none set
  existing: LeaveRequest[]; // the faculty's requests, for the overlap check
}

/** Working days in from..to (0.5 for a half day). */
export const countDays = (from: Dayjs, to: Dayjs, halfDay: LeaveHalfDay | null, workingDays: DayOfWeek[]) => {
  if (halfDay) return 0.5;
  let count = 0;
  for (let d = from.startOf("day"); !d.isAfter(to, "day"); d = d.add(1, "day")) {
    const day = dayOf(d);
    if (day !== "SUNDAY" && workingDays.includes(day)) count++;
  }
  return count;
};

/** The faculty's timetable periods in from..to; a half day counts only periods starting before / from noon. */
export const countPeriods = (from: Dayjs, to: Dayjs, halfDay: LeaveHalfDay | null, timetable: FacultyTimetable | null) => {
  if (!timetable) return 0;
  const starts = new Map(timetable.periods.map((p) => [p.id, p.startTime]));
  const inHalf = (start: string) => !halfDay || (halfDay === "FIRST_HALF" ? start < "12:00" : start >= "12:00");
  // periods per weekday, then add them up over the dates
  const perDay = new Map<string, number>();
  for (const slot of timetable.slots) {
    if (inHalf(starts.get(slot.periodId) ?? "")) perDay.set(slot.day, (perDay.get(slot.day) ?? 0) + 1);
  }
  let count = 0;
  for (let d = from.startOf("day"); !d.isAfter(to, "day"); d = d.add(1, "day")) {
    count += perDay.get(dayOf(d)) ?? 0;
  }
  return count;
};

/** The school's working days from a timetable response (Mon–Sat when no grade periods are set). */
export const workingDaysOf = (timetable: FacultyTimetable | null): DayOfWeek[] =>
  timetable && timetable.days.length > 0 ? timetable.days : [...WORKING_DAYS];

/**
 * Same rules as the backend (LeaveServiceImpl): dates, type and reason required; reason up to 300;
 * To on or after From; inside the active academic year; a half day only on one day; at least one
 * working day; no overlap with a pending / approved leave (two different halves of a day are fine).
 */
export const validateLeave = (form: LeaveForm, context: LeaveRulesContext): LeaveFormErrors => {
  const errors: LeaveFormErrors = {};
  const { fromDate: from, toDate: to } = form;

  if (!from || !from.isValid()) errors.fromDate = "Required";
  if (!to || !to.isValid()) errors.toDate = "Required";
  if (!form.leaveType) errors.leaveType = "Required";
  const reason = form.reason.trim();
  if (!reason) errors.reason = "Required";
  else if (reason.length > LEAVE_TEXT_MAX) errors.reason = `Up to ${LEAVE_TEXT_MAX} characters`;
  if (errors.fromDate || errors.toDate || !from || !to) return errors;

  if (to.isBefore(from, "day")) errors.toDate = "Can't be before From";
  if (!context.year) {
    errors.form = "There is no active academic year.";
  } else if (from.isBefore(context.year.start, "day") || to.isAfter(context.year.end, "day")) {
    errors.form = `Dates must be within ${context.year.name} (${context.year.start.format("D MMM YYYY")} – ${context.year.end.format("D MMM YYYY")}).`;
  }
  if (form.halfDay && !from.isSame(to, "day")) errors.halfDay = "A half day can only be a single day";
  if (errors.toDate || errors.form || errors.halfDay) return errors;

  if (countDays(from, to, null, context.workingDays) === 0) errors.form = "These dates have no working days.";

  const overlap = context.existing.find((other) => {
    if (other.status !== "PENDING" && other.status !== "APPROVED") return false;
    if (dayjs(other.fromDate).isAfter(to, "day") || dayjs(other.toDate).isBefore(from, "day")) return false;
    const otherHalfOfSameDay = form.halfDay && other.halfDay && dayjs(other.fromDate).isSame(from, "day") && other.halfDay !== form.halfDay;
    return !otherHalfOfSameDay;
  });
  if (overlap) {
    errors.form = `Overlaps an existing leave (${dayjs(overlap.fromDate).format("D MMM")}${
      overlap.fromDate !== overlap.toDate ? ` – ${dayjs(overlap.toDate).format("D MMM")}` : ""
    }, ${overlap.status.toLowerCase()}).`;
  }
  return errors;
};
